import {
    ApolloClient,
    InMemoryCache,
    NormalizedCacheObject
} from '@apollo/client';
import { gql } from 'graphql-tag';
import {
    IFlowGalleryTemplate,
    TSortBy
} from '~/components/FlowGallery/types/flow-gallery.types';
import type { RootStore } from '~/stores/root.store';
import { trackError } from '~/services/logger/error.utils';
import { CreateTemplateType } from '~/components/Templates/types/templates.types';
import { IAuthorizedAnalyticsService } from '~/services/analytics/analytics.service';

const FALLBACK_TEMPLATE_ID = '62bc1f3c1f85e6e683c5aa46';
export const PUSH_TO_GALLERY_TEMPLATES_NUMBER = 2;
export const TEMPLATE_IDS_FOR_MSPS = [
    '663a9599df5e72002c3e3afe',
    '663a955ac5c6ef002d43bfb8'
];

export interface IPushToGalleryTemplatesData {
    flows: IFlowGalleryTemplate[];
    fallbackFlow: IFlowGalleryTemplate;
}

export interface IListFlowsRequest {
    tags?: string[];
    industryTags?: string[];
    companyIndustry?: string;
    actionTags?: string[];
    limit?: number;
    searchQuery?: string;
    sortBy?: TSortBy;
}

export const CORE_FLOW_FIELDS = gql`
    fragment CoreFlowFields on Flow {
        _id
        created_at
        published_at
        title
        description
        header_image {
            cloudinary_url
            cloudinary_public_id
            position_h
            position_w
            position_x
            position_y
            crop_ratio
        }
        import_count
        publish_company {
            company_name
        }
        published_template_id
        publisher {
            full_name
            profile_image {
                url
            }
        }
        publisher_website
        industry_tags
        tags
        template {
            flow_content {
                action_block_types
            }
            theme {
                styles {
                    accent_color
                    background_color
                    color
                }
            }
        }
        publisher_website
    }
`;

const LIST_FLOWS = gql`
    ${CORE_FLOW_FIELDS}
    query ListFlows(
        $tags: [String]
        $industryTags: [String]
        $companyIndustry: String
        $actionTags: [String]
        $limit: Int
        $searchQuery: String
        $sortBy: String
        $sortOrder: String
    ) {
        flows(
            tags: $tags
            industryTags: $industryTags
            companyIndustry: $companyIndustry
            actionTags: $actionTags
            limit: $limit
            searchQuery: $searchQuery
            sortBy: $sortBy
            sortOrder: $sortOrder
        ) {
            ...CoreFlowFields
        }
    }
`;

const LIST_PUSH_TO_GALLERY_FLOWS = gql`
    ${CORE_FLOW_FIELDS}
    query ListPushToGalleryFlows(
        $industry: String
        $limit: Int
        $fallbackTemplateId: ID!
    ) {
        flows: flows(
            industryTags: [$industry]
            limit: $limit
            sortBy: "relevance"
            sortOrder: "desc"
            companyIndustry: $industry
        ) {
            ...CoreFlowFields
        }
        fallback: flow(flowId: $fallbackTemplateId) {
            ...CoreFlowFields
        }
    }
`;

const LIST_PUSH_TO_GALLERY_FLOWS_BY_IDS = gql`
    ${CORE_FLOW_FIELDS}
    query ListPushToGalleryFlowsByIds($ids: [ID]!) {
        flowsByIds: flowsByIds(flowIds: $ids) {
            ...CoreFlowFields
        }
    }
`;

const CUSTOM_TEMPLATE = gql`
    query Flows($tags: [String], $sortBy: String, $sortOrder: String) {
        flows(tags: $tags, sortBy: $sortBy, sortOrder: $sortOrder) {
            _id
            created_at
            published_at
            title
            description
            header_image {
                cloudinary_url
                cloudinary_public_id
                position_h
                position_w
                position_x
                position_y
                crop_ratio
            }
            import_count
            publish_company {
                company_name
            }
            published_template_id
            publisher {
                full_name
                profile_image {
                    url
                }
            }
            publisher_website
            industry_tags
            tags
            template {
                flow_content {
                    action_block_types
                }
                theme {
                    styles {
                        accent_color
                        background_color
                        color
                    }
                }
            }
            publisher_website
        }
    }
`;

const DOWNLOAD_FLOW = gql`
    query DownloadFlow($flowId: ID!) {
        flow(flowId: $flowId) {
            _id
            published_template_id
            title
            header_image {
                cloudinary_url
                cloudinary_public_id
                position_h
                position_w
                position_x
                position_y
                crop_ratio
            }
        }
    }
`;

const LIST_FILTERS = gql`
    query ListFilters {
        filters {
            name
            key
            tags
        }
    }
`;

const blocksMapping = {
    MoodBoard: ['mood board'],
    ContentApproval: ['proofing'],
    Invoice: ['invoice', 'proposal'],
    Brochures: ['brochure', 'welcome guide', 'proposal'],
    Testimonial: ['testimonials', 'feedback'],
    Questionnaire: ['questionnaire', 'form', 'questions', 'feedback'],
    Contract: ['contract', 'proposal'],
    Proposal: ['proposal']
};

export class FlowGalleryService {
    rootStore: RootStore;
    client: ApolloClient<NormalizedCacheObject>;
    analyticsService: IAuthorizedAnalyticsService;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async listFlows({
        sortBy: _sortBy,
        ...variables
    }: IListFlowsRequest): Promise<IFlowGalleryTemplate[]> {
        const { analyticsService } = this.rootStore;
        try {
            if (_sortBy) {
                const [sortBy, sortOrder] = _sortBy.split('-');
                Object.assign(variables, { sortBy, sortOrder });
            }
            const eventPlanningIndex =
                variables.industryTags?.indexOf('event_planning');
            if (eventPlanningIndex > -1) {
                const _industryTags = [...variables.industryTags];
                _industryTags[eventPlanningIndex] = 'event_planner';
                variables.industryTags = _industryTags;
            }
            analyticsService.track(
                '[Template gallery] before list flows request',
                { variables }
            );
            const response = await this.getClient().query({
                query: LIST_FLOWS,
                variables,
                errorPolicy: 'all'
            });
            analyticsService.track(
                '[Template gallery] after list flows request',
                {
                    templatesLength: response?.data?.flows?.length
                }
            );

            const { errors } = response;
            if (errors) {
                this.trackErrorListFlowRequest(errors);
            }
            return response.data?.flows.filter(flow => !!flow);
        } catch (err) {
            trackError({
                error: err,
                message:
                    '[Template gallery] error trying to fetch templates from gpl microservice'
            });
        }
    }

    async downloadFlow({ flowId }) {
        const response = await this.getClient().query({
            query: DOWNLOAD_FLOW,
            variables: { flowId }
        });
        return response.data.flow;
    }

    async listFilters() {
        try {
            const response = await this.getClient().query({
                query: LIST_FILTERS
            });
            return response.data?.filters;
        } catch (err) {
            this.trackErrorListFlowRequest(err);
        }
    }

    async listSuggestedTemplates(
        industry,
        blocks
    ): Promise<IFlowGalleryTemplate[]> {
        // return true if the template contains specific block
        const checkBlock = (blockTags, templateTags) => {
            return templateTags.some(tag => {
                return blockTags?.includes(tag);
            });
        };

        // return true if all the blocks exists in the template
        const checkBlocks = (blocksTags, templateTags) => {
            return blocksTags.every(blockTags => {
                return checkBlock(blockTags, templateTags);
            });
        };

        if (!blocks?.length) {
            return null;
        }

        const tagsToCheck = blocks.map(block => {
            return blocksMapping[block];
        });

        const response = await this.getClient().query({
            query: CUSTOM_TEMPLATE,
            variables: {
                tags: blocksMapping[blocks[0]],
                sortBy: 'popularity',
                sortOrder: 'desc'
            }
        });

        // filter only the templates that contains all the blocks
        const filteredTemplates = response.data.flows.filter(template => {
            return checkBlocks(tagsToCheck, template.tags);
        });

        if (filteredTemplates?.length == 0) {
            return null;
        }

        const templates = [];

        const mostRelevant = filteredTemplates.find(template => {
            return template.industry_tags.includes(industry);
        });
        if (mostRelevant) {
            templates.push(mostRelevant);
        } else {
            templates.push(filteredTemplates[0]);
        }
        if (filteredTemplates.length > 1) {
            if (templates[0]._id == filteredTemplates[0]._id) {
                templates.push(filteredTemplates[1]);
            } else {
                templates.push(filteredTemplates[0]);
            }
        }
        return templates;
    }

    getActionsToShow(selectedList: CreateTemplateType[]) {
        const actions = [];
        if (selectedList.includes(CreateTemplateType.Brochures)) {
            actions.push('service_selection_block');
        }
        if (selectedList.includes(CreateTemplateType.Invoice)) {
            actions.push('invoice_container_block');
            actions.push('charge_container_block');
        }
        if (selectedList.includes(CreateTemplateType.Contract)) {
            actions.push('contract_block');
        }
        return actions;
    }

    async listPushToGalleryTemplates(
        industry: string
    ): Promise<IPushToGalleryTemplatesData> {
        try {
            const response = await this.getClient().query({
                query: LIST_PUSH_TO_GALLERY_FLOWS,
                variables: {
                    industry,
                    limit: PUSH_TO_GALLERY_TEMPLATES_NUMBER,
                    fallbackTemplateId: FALLBACK_TEMPLATE_ID
                },
                errorPolicy: 'all'
            });

            const { errors } = response;
            if (errors) {
                this.trackErrorListFlowRequest(errors);
            }

            return {
                flows: response.data?.flows?.filter(flow => !!flow),
                fallbackFlow: response.data?.fallback
            };
        } catch (error) {
            trackError({ error });
            return Promise.resolve({ flows: [], fallbackFlow: null });
        }
    }

    async listFlowsByIDs(ids: string[]): Promise<IPushToGalleryTemplatesData> {
        try {
            const response = await this.getClient().query({
                query: LIST_PUSH_TO_GALLERY_FLOWS_BY_IDS,
                variables: {
                    ids,
                    limit: PUSH_TO_GALLERY_TEMPLATES_NUMBER,
                    fallbackTemplateId: FALLBACK_TEMPLATE_ID
                },
                errorPolicy: 'all'
            });

            const { errors } = response;
            if (errors) {
                this.trackErrorListFlowRequest(errors);
                throw new Error();
            }

            return {
                flows: response.data?.flowsByIds?.filter(flow => !!flow),
                fallbackFlow: response.data?.fallback
            };
        } catch (error) {
            trackError({ error });
            return Promise.reject();
        }
    }

    trackErrorListFlowRequest(errors) {
        const context = errors.length === 1 ? errors[0] : { ...errors };
        trackError({
            error: new Error('A GQL error(s) on Template Gallery microservice'),
            context
        });
    }

    protected getBaseUrl() {
        const { configStore } = this.rootStore;
        return configStore.config.flow_gallery_url;
    }

    protected getClient() {
        if (!this.client) {
            this.client = new ApolloClient({
                uri: `${this.getBaseUrl()}/graphql`,
                cache: new InMemoryCache()
            });
        }
        return this.client;
    }
}
